import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SiteProvider } from "@/context/SiteContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Features from "@/pages/Features";
import Commands from "@/pages/Commands";
import Docs from "@/pages/Docs";
import StatusPage from "@/pages/StatusPage";
import Changelog from "@/pages/Changelog";
import Support from "@/pages/Support";
import Premium from "@/pages/Premium";
import Boosters from "@/pages/Boosters";
import Dashboard from "@/pages/Dashboard";
import PaymentResult from "@/pages/PaymentResult";
import Login from "@/pages/Login";
import Account from "@/pages/Account";
import AdminLayout from "@/pages/admin/AdminLayout";
import DashboardAdmin from "@/pages/admin/Dashboard";
import Analytics from "@/pages/admin/Analytics";
import Servers from "@/pages/admin/Servers";
import UsersAdmin from "@/pages/admin/UsersAdmin";
import MusicAdmin from "@/pages/admin/MusicAdmin";
import CommandsAdmin from "@/pages/admin/CommandsAdmin";
import ReviewsAdmin from "@/pages/admin/ReviewsAdmin";
import Announcements from "@/pages/admin/Announcements";
import WebsiteAdmin from "@/pages/admin/WebsiteAdmin";
import DocsAdmin from "@/pages/admin/DocsAdmin";
import ChangelogAdmin from "@/pages/admin/ChangelogAdmin";
import Tickets from "@/pages/admin/Tickets";
import Moderation from "@/pages/admin/Moderation";
import BoostersAdmin from "@/pages/admin/BoostersAdmin";
import Staff from "@/pages/admin/Staff";
import StatusAdmin from "@/pages/admin/StatusAdmin";
import PremiumAdmin from "@/pages/admin/PremiumAdmin";
import AIAdmin from "@/pages/admin/AIAdmin";
import IntegrationsAdmin from "@/pages/admin/IntegrationsAdmin";
import LogsAdmin from "@/pages/admin/LogsAdmin";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";
import AuditLog from "@/pages/admin/AuditLog";
import Owner from "@/pages/admin/Owner";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PublicShell() {
  return (
    <>
      <Navbar />
      <Outlet />
      <Footer />
    </>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <SiteProvider>
          <BrowserRouter>
            <ScrollToTop />
            <div className="noise-overlay" />
            <Routes>
              <Route element={<PublicShell />}>
                <Route path="/" element={<Home />} />
                <Route path="/features" element={<Features />} />
                <Route path="/commands" element={<Commands />} />
                <Route path="/docs" element={<Docs />} />
                <Route path="/docs/:slug" element={<Docs />} />
                <Route path="/status" element={<StatusPage />} />
                <Route path="/changelog" element={<Changelog />} />
                <Route path="/support" element={<Support />} />
                <Route path="/premium" element={<Premium />} />
                <Route path="/account" element={<Account />} />
                <Route path="/boosters" element={<Boosters />} />
                <Route path="/dashboard" element={<Dashboard />} />
              </Route>
              <Route path="/payment/success" element={<PaymentResult variant="success" />} />
              <Route path="/payment/cancel" element={<PaymentResult variant="cancel" />} />
              <Route path="/login" element={<Login />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<DashboardAdmin />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="servers" element={<Servers />} />
                <Route path="users" element={<UsersAdmin />} />
                <Route path="music" element={<MusicAdmin />} />
                <Route path="commands" element={<CommandsAdmin />} />
                <Route path="reviews" element={<ReviewsAdmin />} />
                <Route path="announcements" element={<Announcements />} />
                <Route path="website" element={<WebsiteAdmin />} />
                <Route path="docs" element={<DocsAdmin />} />
                <Route path="changelog" element={<ChangelogAdmin />} />
                <Route path="tickets" element={<Tickets />} />
                <Route path="boosters" element={<BoostersAdmin />} />
                <Route path="moderation" element={<Moderation />} />
                <Route path="staff" element={<Staff />} />
                <Route path="status" element={<StatusAdmin />} />
                <Route path="premium" element={<PremiumAdmin />} />
                <Route path="ai" element={<AIAdmin />} />
                <Route path="integrations" element={<IntegrationsAdmin />} />
                <Route path="logs" element={<LogsAdmin />} />
                <Route path="settings" element={<SettingsAdmin />} />
                <Route path="audit" element={<AuditLog />} />
                <Route path="owner" element={<Owner />} />
              </Route>
              <Route path="*" element={<Home />} />
            </Routes>
          </BrowserRouter>
          <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#0F0F14", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" } }} />
        </SiteProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
